import React, { useState } from "react";
import { useS3Analytics, useDateRange } from "../../hooks/use-s3-analytics";
import { useAuthStore } from "../../stores/auth-store";
import { TopPerformingAssets } from "../../components/analytics/top-performing-assets";

export function AnalyticsPage() {
  // Get tenant ID from auth context
  const { user, isAuthenticated, isLoading } = useAuthStore();

  // For SUPER_ADMIN users, use "GLOBAL" as tenant ID, otherwise use user's tenant ID
  const tenantId =
    user?.role === "SUPER_ADMIN"
      ? "GLOBAL"
      : user?.tenantId || "default-tenant";

  console.log("Analytics page - User:", user);
  console.log("Analytics page - Tenant ID:", tenantId);

  // Date range management
  const {
    dateRange,
    setLast7Days,
    setLast30Days,
    setLast90Days,
    setCustomRange,
  } = useDateRange();

  // Analytics data
  const {
    dashboardMetrics,
    dashboardLoading,
    dashboardError,
    topLinks,
    topLinksLoading,
    hourlyTrends,
    realTimeMetrics,
    refreshDashboard,
    isRefreshing,
    lastUpdated,
    clearCache,
    clearCacheLoading,
  } = useS3Analytics({
    tenantId,
    dateRange,
    autoRefresh: true,
    enableRealTime: true,
  });

  // Authentication loading state
  if (isLoading || !isAuthenticated) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
        <div className="ml-4 text-gray-600">
          {isLoading ? "Loading..." : "Please log in"}
        </div>
      </div>
    );
  }

  // Loading state
  if (dashboardLoading && !dashboardMetrics) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  // Error state
  if (dashboardError && !dashboardMetrics) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="flex">
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">
              Error loading analytics data
            </h3>
            <div className="mt-2 text-sm text-red-700">
              {dashboardError.message || "An unexpected error occurred"}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="border-b border-gray-200 pb-4">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Analytics</h1>
            <p className="mt-2 text-gray-600">
              Comprehensive performance monitoring and insights across all
              conversations and components.
            </p>
          </div>
          <div className="flex items-center space-x-4">
            {/* Date Range Selector */}
            <div className="flex space-x-2">
              <button
                onClick={setLast7Days}
                className="px-3 py-1 text-sm border rounded hover:bg-gray-50"
              >
                Last 7 days
              </button>
              <button
                onClick={setLast30Days}
                className="px-3 py-1 text-sm border rounded hover:bg-gray-50"
              >
                Last 30 days
              </button>
              <button
                onClick={setLast90Days}
                className="px-3 py-1 text-sm border rounded hover:bg-gray-50"
              >
                Last 90 days
              </button>
            </div>

            {/* Refresh Button */}
            <button
              onClick={refreshDashboard}
              disabled={isRefreshing}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
            >
              {isRefreshing ? "Refreshing..." : "Refresh"}
            </button>

            {/* Clear Cache Button */}
            {/* <button
              onClick={clearCache}
              disabled={clearCacheLoading}
              className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
            >
              {clearCacheLoading ? "Clearing..." : "Clear Cache"}
            </button> */}
          </div>
        </div>

        {/* Last Updated */}
        {lastUpdated && (
          <div className="mt-2 text-sm text-gray-500">
            Last updated: {lastUpdated.toLocaleString()}
          </div>
        )}
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg shadow border">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                  />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">
                Total Interactions
              </p>
              <p className="text-2xl font-semibold text-gray-900">
                {dashboardMetrics?.totalClicks?.toLocaleString() || "0"}
              </p>
              {realTimeMetrics && (
                <p className="text-sm text-blue-600">
                  +{realTimeMetrics.totalClicks} today
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow border">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">
                Unique Visitors
              </p>
              <p className="text-2xl font-semibold text-gray-900">
                {dashboardMetrics?.uniqueClicks?.toLocaleString() || "0"}
              </p>
              <p className="text-sm text-gray-500">
                {dashboardMetrics?.conversionRate?.toFixed(1) || "0"}%
                conversion rate
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow border">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
                  />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">
                Mobile Traffic
              </p>
              <p className="text-2xl font-semibold text-gray-900">
                {dashboardMetrics?.deviceBreakdown
                  ? Math.round(
                      (dashboardMetrics.deviceBreakdown.MOBILE /
                        (dashboardMetrics.deviceBreakdown.MOBILE +
                          dashboardMetrics.deviceBreakdown.DESKTOP +
                          dashboardMetrics.deviceBreakdown.TABLET +
                          dashboardMetrics.deviceBreakdown.OTHER)) *
                        100
                    ) + "%"
                  : "0%"}
              </p>
              <p className="text-sm text-gray-500">
                {dashboardMetrics?.deviceBreakdown?.MOBILE?.toLocaleString() ||
                  "0"}{" "}
                mobile clicks
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow border">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                  />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Active Links</p>
              <p className="text-2xl font-semibold text-gray-900">
                {dashboardMetrics?.activeLinks?.toLocaleString() || "0"}
              </p>
              <p className="text-sm text-gray-500">
                {dashboardMetrics?.totalLinks?.toLocaleString() || "0"} total
                links
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Performing Assets */}
        <TopPerformingAssets
          tenantId={tenantId}
          dateRange={dateRange}
          limit={10}
        />

        {/* Device Breakdown */}
        <div className="bg-white rounded-lg shadow border">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Device Breakdown
            </h2>
          </div>
          <div className="p-6">
            {dashboardMetrics?.deviceBreakdown ? (
              <div className="space-y-4">
                {Object.entries(dashboardMetrics.deviceBreakdown).map(
                  ([device, count], index) => {
                    const total = Object.values(
                      dashboardMetrics.deviceBreakdown
                    ).reduce((sum, val) => sum + val, 0);
                    const percentage = total > 0 ? (count / total) * 100 : 0;
                    const colors = [
                      "bg-blue-500",
                      "bg-green-500",
                      "bg-purple-500",
                      "bg-orange-500",
                    ];
                    const color = colors[index % colors.length];

                    return (
                      <div key={device}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <div
                              className={`w-3 h-3 ${color} rounded-full mr-3`}
                            ></div>
                            <span className="text-gray-700 capitalize">
                              {device.toLowerCase()}
                            </span>
                          </div>
                          <div className="text-right">
                            <span className="font-semibold text-gray-900">
                              {percentage.toFixed(1)}%
                            </span>
                            <div className="text-sm text-gray-500">
                              {count.toLocaleString()} clicks
                            </div>
                          </div>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                          <div
                            className={`${color} h-2 rounded-full`}
                            style={{ width: `${percentage}%` }}
                          ></div>
                        </div>
                      </div>
                    );
                  }
                )}
              </div>
            ) : (
              <div className="text-center text-gray-500 py-8">
                No device data available
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Top Performing Links */}
      <div className="bg-white rounded-lg shadow border">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            Top Performing Links
          </h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Link
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Short URL
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total Clicks
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Unique Clicks
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Conversion Rate
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Performance
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {topLinks && topLinks.length > 0 ? (
                topLinks.map((link: any, index: number) => (
                  <tr key={link.linkId} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-8 w-8">
                          <div className="h-8 w-8 rounded-lg bg-blue-100 flex items-center justify-center">
                            <svg
                              className="h-4 w-4 text-blue-600"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                              />
                            </svg>
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            Link #{index + 1}
                          </div>
                          <div className="text-sm text-gray-500">
                            {link.linkId.substring(0, 8)}...
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 font-mono">
                        {link.shortUrl || "N/A"}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {link.totalClicks?.toLocaleString() || "0"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {link.uniqueClicks?.toLocaleString() || "0"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {link.conversionRate
                        ? `${link.conversionRate.toFixed(1)}%`
                        : "N/A"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 w-10 h-2 bg-gray-200 rounded-full mr-2">
                          <div
                            className="h-2 bg-green-500 rounded-full"
                            style={{
                              width: `${Math.min(
                                100,
                                (link.totalClicks /
                                  (topLinks[0]?.totalClicks || 1)) *
                                  100
                              )}%`,
                            }}
                          ></div>
                        </div>
                        <span className="text-sm text-gray-600">
                          {Math.round(
                            (link.totalClicks /
                              (topLinks[0]?.totalClicks || 1)) *
                              100
                          )}
                          %
                        </span>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={6}
                    className="px-6 py-8 text-center text-gray-500"
                  >
                    {topLinksLoading ? (
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                        <span className="ml-2">
                          Loading top performing links...
                        </span>
                      </div>
                    ) : (
                      "No link data available"
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-lg shadow border">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            Recent Activity
          </h2>
        </div>
        <div className="p-6">
          {dashboardMetrics?.recentActivity &&
          dashboardMetrics.recentActivity.length > 0 ? (
            <div className="space-y-4">
              {dashboardMetrics.recentActivity
                .slice(0, 10)
                .map((activity: any, index: number) => {
                  const colors = [
                    "bg-green-500",
                    "bg-blue-500",
                    "bg-purple-500",
                    "bg-orange-500",
                  ];
                  const color = colors[index % colors.length];
                  const timeAgo = new Date(activity.timestamp).toLocaleString();

                  return (
                    <div
                      key={activity.eventId}
                      className="flex items-start space-x-3"
                    >
                      <div
                        className={`flex-shrink-0 w-2 h-2 ${color} rounded-full mt-2`}
                      ></div>
                      <div className="flex-1">
                        <p className="text-sm text-gray-900">
                          <span className="font-medium">
                            {activity.linkId.substring(0, 8)}...
                          </span>{" "}
                          {activity.eventType.toLowerCase().replace("_", " ")}{" "}
                          from {activity.deviceType.toLowerCase()}
                        </p>
                        <p className="text-xs text-gray-500">{timeAgo}</p>
                      </div>
                    </div>
                  );
                })}
            </div>
          ) : (
            <div className="text-center text-gray-500 py-8">
              {dashboardLoading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                  <span className="ml-2">Loading recent activity...</span>
                </div>
              ) : (
                "No recent activity available"
              )}
            </div>
          )}
        </div>
      </div>

      {/* Real-time Status */}
      {/* {realTimeMetrics && (
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">
                Real-time Data Active
              </h3>
              <div className="mt-1 text-sm text-blue-700">
                Today: {realTimeMetrics.totalClicks} clicks,{" "}
                {realTimeMetrics.uniqueClicks} unique visitors
              </div>
            </div>
          </div>
        </div>
      )} */}
    </div>
  );
}
